import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Download,
  Info,
  Pencil,
  RotateCw,ArrowUpCircle
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { useUpgradeStore } from '../stores/upgradeStore';
import toast from 'react-hot-toast';

export const BulkAnswerStatusPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { requests, approveRequest, rejectRequest } = useUpgradeStore();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPreparing, setIsPreparing] = useState(true);
  const [results, setResults] = useState<Array<{
    passportNumber: string;
    hotelName: string;
    status: 'approve' | 'reject' | 'change' | 'error';
    price?: number;
    previousPrice?: number;
    message: string;
    requestId?: string;
  }>>([]);
  const [stats, setStats] = useState({
    approved: 0,
    rejected: 0,
    changed: 0,
    errors: 0,
    total: 0
  });

  // Get file data from location state
  const fileData = location.state?.fileData;

  // Load requests and process file
  useEffect(() => {
    if (!fileData) {
      setIsPreparing(false);
      return;
    }
    
    const processFile = async () => {
      try {
        console.log('Starting file processing');
        setIsPreparing(true);
        
        // Parse the Excel file
        const wb = XLSX.read(fileData, { type: 'binary' });
        const firstSheet = wb.SheetNames[0];
        const ws = wb.Sheets[firstSheet];
        const data = XLSX.utils.sheet_to_json(ws);
        
        console.log(`File parsed successfully. Found ${data.length} rows`);
        
        // Wait for requests to be loaded
        if (Object.keys(requests).length === 0) {
          console.log('Waiting for requests to be loaded');
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
        
        // Process rows and match with requests
        console.log(`Processing with ${Object.keys(requests).length} available requests`);
        const processResults: typeof results = [];
        
        for (const row of data) {
          try {
            const rowData: any = row;
            console.log('Processing row:', rowData);
            
            // Extract information
            const passportNumber = rowData.passport_number || rowData['Passport Number'];
            const hotelName = rowData.hotel_name || rowData['Hotel Name'];
            const statusInput = (rowData.status?.toString() || '').trim().toLowerCase();
            const priceInput = rowData.price;
            
            if (!passportNumber || !hotelName) {
              console.error('Missing passport or hotel:', rowData);
              processResults.push({
                passportNumber: passportNumber || 'Unknown',
                hotelName: hotelName || 'Unknown',
                status: 'error',
                message: 'Missing required fields'
              });
              continue;
            }
            
            // Find matching requests
            const matchingRequests = Object.values(requests).filter(request => 
              request.passportNumber === passportNumber && 
              request.fromHotel === hotelName
            );
            
            console.log(`Found ${matchingRequests.length} matching requests for passport ${passportNumber} at hotel ${hotelName}`);
            
            if (matchingRequests.length === 0) {
              processResults.push({
                passportNumber,
                hotelName,
                status: 'error',
                message: 'No matching request found'
              });
              continue;
            }
            
            // Get the most recent request (usually the first in sorting of newest first)
            const targetRequest = matchingRequests[0];
            
            // Process based on status
            if (statusInput === 'approve' || statusInput === 'change') {
              // For approval or change, we need a valid price
              const price = parseFloat(priceInput);
              const oldPrice = targetRequest.price;
              
              if (isNaN(price) || price <= 0) {
                processResults.push({
                  passportNumber,
                  hotelName,
                  status: 'error',
                  requestId: targetRequest.id,
                  message: 'Invalid price for approval'
                });
                continue;
              }
              
              // For status "change", the request must already be approved with a price
              if (statusInput === 'change' && (!targetRequest.status || targetRequest.status !== 'approved' || !targetRequest.price)) {
                processResults.push({
                  passportNumber,
                  hotelName,
                  status: 'error',
                  requestId: targetRequest.id,
                  message: 'Request is not in approved status with a price'
                });
                continue;
              }
              
              processResults.push({
                passportNumber,
                hotelName,
                status: statusInput as 'approve' | 'change',
                price,
                previousPrice: oldPrice,
                requestId: targetRequest.id,
                message: statusInput === 'approve' ? 
                  `Will approve with price ${price} SAR` : 
                  `Will change price from ${oldPrice} to ${price} SAR`
              });
            } 
            else if (statusInput === 'reject') {
              // For rejection, we need a reason
              const reason = rowData.reason || 'Rejected through bulk processor';
              
              processResults.push({
                passportNumber,
                hotelName,
                status: 'reject',
                requestId: targetRequest.id,
                message: `Will reject with reason: ${reason}`
              });
            }
            else {
              // Invalid status
              processResults.push({
                passportNumber,
                hotelName,
                status: 'error',
                requestId: targetRequest.id,
                message: `Invalid status: ${statusInput}. Must be "approve", "reject", or "change"`
              });
            }
          } catch (err) {
            console.error('Error processing row:', err, row);
            processResults.push({
              passportNumber: row.passport_number || 'Unknown',
              hotelName: row.hotel_name || 'Unknown',
              status: 'error',
              message: `Error: ${err.message || 'Unknown error'}`
            });
          }
        }
        
        // Calculate stats
        const newStats = processResults.reduce((acc, result) => {
          acc.total++;
          if (result.status === 'approve') acc.approved++;
          else if (result.status === 'reject') acc.rejected++;
          else if (result.status === 'change') acc.changed++;
          else if (result.status === 'error') acc.errors++;
          return acc;
        }, { approved: 0, rejected: 0, changed: 0, errors: 0, total: 0 });
        
        console.log('Processing complete with stats:', newStats);
        
        setResults(processResults);
        setStats(newStats);
        setIsPreparing(false);
      } catch (error) {
        console.error('Error processing file:', error);
        toast.error('Error processing file');
        setIsPreparing(false);
      }
    };

    processFile();
  }, [fileData, requests]);

  // Process all approved results
  const handleProcessAll = async () => {
    try {
      setIsProcessing(true);
      let processed = 0;
      
      // Process each result
      for (const result of results) {
        try {
          if (result.status === 'approve' && result.requestId && result.price) {
            await approveRequest(result.requestId, result.price);
            processed++;
          } 
          else if (result.status === 'change' && result.requestId && result.price) {
            // For "change", we re-approve with the new price
            await approveRequest(result.requestId, result.price);
            processed++;
          }
          else if (result.status === 'reject' && result.requestId) {
            await rejectRequest(result.requestId, 'Rejected through bulk processor');
            processed++;
          }
        } catch (error) {
          console.error(`Error processing request ${result.requestId}:`, error);
        }
      }
      
      toast.success(`Processed ${processed} requests successfully`);
      
      // Navigate back to room upgrade page
      navigate('/room-upgrade');
    } catch (error) {
      console.error('Error processing requests:', error);
      toast.error('Error processing requests');
    } finally {
      setIsProcessing(false);
    }
  };

  // Export results to Excel
  const handleExportResults = () => {
    try {
      const exportData = results.map(result => ({
        'Passport Number': result.passportNumber,
        'Hotel Name': result.hotelName,
        'Status': result.status,
        'Price': result.price || '',
        'Previous Price': result.previousPrice || '',
        'Message': result.message
      }));
      
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Results');
      
      const filename = `bulk-answer-results-${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, filename);
      
      toast.success('Results exported successfully');
    } catch (error) {
      console.error('Error exporting results:', error);
      toast.error('Error exporting results');
    }
  };

  if (!fileData) {
    // Redirect back if no file data
    navigate('/room-upgrade');
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white p-6 rounded-xl shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ArrowUpCircle className="text-primary-500" />
            Bulk Answer Status
          </h1>
          <p className="text-gray-500 mt-1">Process bulk upgrade request answers</p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/room-upgrade')}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <ArrowLeft size={18} />
            <span>Back</span>
          </button>
          
          <button
            onClick={handleExportResults}
            disabled={results.length === 0 || isPreparing}
            className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 disabled:opacity-50"
          >
            <Download size={18} />
            <span className="hidden sm:inline">Export Results</span>
          </button>
          
          <button
            onClick={handleProcessAll}
            disabled={isProcessing || isPreparing || stats.total === 0}
            className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
          >
            {isProcessing ? (
              <>
                <RotateCw size={18} className="animate-spin" />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <CheckCircle size={18} />
                <span>Process All</span>
              </>
            )}
          </button>
        </div>
      </motion.div>
      
      {/* Stats Panel */}
      {!isPreparing && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4"
        >
          <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
            <div className="text-sm text-gray-600 mb-1">Total Requests</div>
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          </div>
          
          <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
            <div className="text-sm text-gray-600 mb-1">To Approve</div>
            <div className="text-2xl font-bold text-green-600">{stats.approved}</div>
          </div>
          
          <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
            <div className="text-sm text-gray-600 mb-1">To Reject</div>
            <div className="text-2xl font-bold text-red-600">{stats.rejected}</div>
          </div>
          
          <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
            <div className="text-sm text-gray-600 mb-1">Price Changes</div>
            <div className="text-2xl font-bold text-blue-600">{stats.changed}</div>
          </div>
          
          <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
            <div className="text-sm text-gray-600 mb-1">Errors</div>
            <div className="text-2xl font-bold text-yellow-600">{stats.errors}</div>
          </div>
        </motion.div>
      )}
      
      {/* Info Panel */}
      {!isPreparing && stats.total > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-blue-50 p-5 rounded-xl border border-blue-100"
        >
          <div className="flex items-start gap-3">
            <Info className="text-blue-500 shrink-0 mt-1" size={20} />
            <div>
              <h3 className="font-medium text-blue-800">Processing Information</h3>
              <p className="text-sm text-blue-700 mt-1">
                Review the table below before processing. All valid entries will be processed when you click "Process All".
                Entries with errors will be skipped. You can export the results for your records.
              </p>
            </div>
          </div>
        </motion.div>
      )}
      
      {/* Results Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm overflow-hidden"
      >
        {isPreparing ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mb-4"></div>
            <p className="text-gray-500">Processing file data...</p>
          </div>
        ) : results.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Passport Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hotel
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Message
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {results.map((result, index) => (
                  <tr key={index} className={
                    result.status === 'error' ? 'bg-red-50' : 
                    result.status === 'approve' ? 'bg-green-50' :
                    result.status === 'change' ? 'bg-blue-50' :
                    'bg-yellow-50'
                  }>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {result.passportNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {result.hotelName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        result.status === 'error' ? 'bg-red-100 text-red-800' : 
                        result.status === 'approve' ? 'bg-green-100 text-green-800' :
                        result.status === 'change' ? 'bg-blue-100 text-blue-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {result.status === 'error' ? <AlertTriangle size={12} className="mr-1" /> : 
                         result.status === 'approve' ? <CheckCircle size={12} className="mr-1" /> :
                         result.status === 'change' ? <Pencil size={12} className="mr-1" /> :
                         <XCircle size={12} className="mr-1" />}
                        {result.status.charAt(0).toUpperCase() + result.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {result.status === 'change' ? (
                        <span>
                          <span className="line-through text-gray-500">{result.previousPrice} SAR</span>
                          {' → '}
                          <span className="font-medium text-blue-600">{result.price} SAR</span>
                        </span>
                      ) : result.price ? (
                        <span className="font-medium">{result.price} SAR</span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {result.message}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500">
            No results to display. Please process a file first.
          </div>
        )}
      </motion.div>
    </div>
  );
};